import {
  addAdditionalSlotsForCleaning,
  parseTimeIntervals,
} from './CommonMethods'
import { HOURS_IN_A_WEEK } from './GenerateKitchenRows'
import {
  ChangeoverWireEvent,
  describeChangeoverEvent,
  pushChangeoverDetail,
} from './ChangeoverReason'
import {
  AdditionalSlotsType,
  ChangeoverDetail,
  ChangeoverRow,
  InputRow,
  kTypeCleaning,
} from './SchedulerView.types'

export const CHANGEOVER = 'changeover'

const defaultData: ChangeoverRow = {
  base_material: [],
  base_material_description: [],
  value: 0,
  cleaning_start_time: 0,
  cleaning_end_time: 0,
  preclean_start_time: 0,
  preclean_end_time: 0,
  changeover_start_time: 0,
  changeover_end_time: 0,
  changeover_details: [],
  slot_types: [CHANGEOVER],
  down_time_start_time: 0,
  down_time_end_time: 0,
}

// The display name lives in `changeover_asset`; `asset_id` is the underlying
// interned id used as a fallback when the lookup table for asset names is
// empty. The rest of the wire shape, and the `reason` parsing, is shared with
// the Line/Kitchen grids and the factory Gantt — see `ChangeoverReason.ts`.
type ChangeoverEvent = ChangeoverWireEvent

const eventAssetName = (evt: ChangeoverEvent): string | undefined =>
  evt.changeover_asset ?? evt.asset_id

const parseEvents = (
  raw: string | object | null | undefined
): ChangeoverEvent[] => {
  if (!raw) return []
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return Array.isArray(parsed) ? (parsed as ChangeoverEvent[]) : []
  } catch {
    return []
  }
}

export const generateChangeoverRows = (
  data: InputRow[],
  additionalSlots?: AdditionalSlotsType[]
) => {
  const changeoverData: kTypeCleaning<ChangeoverRow> = {}

  data?.forEach((row: InputRow) => {
    // Kitchen-side changeover events
    if (row.kitchen) {
      parseEvents(row.additional_kitchen_changeovers).forEach((evt) => {
        const assetName = eventAssetName(evt)
        if (!assetName) return
        addSlots(
          Math.floor(evt.start),
          Math.ceil(evt.end),
          changeoverData,
          assetName,
          row,
          row.kitchen,
          evt.start,
          evt.end,
          describeChangeoverEvent(evt, row.kitchen)
        )
      })
    }

    // Line-side changeover events
    if (row.line) {
      parseEvents(row.additional_line_changeovers).forEach((evt) => {
        const assetName = eventAssetName(evt)
        if (!assetName) return
        addSlots(
          Math.floor(evt.start),
          Math.ceil(evt.end),
          changeoverData,
          assetName,
          row,
          row.line,
          evt.start,
          evt.end,
          describeChangeoverEvent(evt, row.line)
        )
      })
    }
  })

  if (additionalSlots && additionalSlots.length > 0) {
    additionalSlots.forEach((slot) => {
      const { resource } = slot
      const slots = parseTimeIntervals(slot.downTime)
      addAdditionalSlotsForCleaning<ChangeoverRow>(
        changeoverData,
        slots,
        resource,
        defaultData
      )
    })
  }

  // Fill empty hours with default data so every cell has a value (matches CleaningRows behaviour)
  const changeoverRowData = []
  for (const asset in changeoverData) {
    const currentAsset = changeoverData[asset]
    const resources = currentAsset.resources
    for (const resource in resources) {
      const resourceData = resources[resource]
      for (let i = 0; i < HOURS_IN_A_WEEK; i++) {
        if (!resourceData[`hour-${i}`]) {
          resourceData[`hour-${i}`] = defaultData
        }
      }
      changeoverRowData.push({
        ...resourceData,
        id: `${asset}-${resource}`,
        resource,
        cip: asset,
      })
    }
  }
  return changeoverRowData
}

const addSlots = (
  startHour: number,
  endHour: number,
  changeoverData: kTypeCleaning<ChangeoverRow>,
  asset: string,
  row: InputRow,
  resource: string,
  startTime: number,
  endTime: number,
  detail: ChangeoverDetail
) => {
  const runRateBase = row.runRateBase
  for (let i = startHour; i < endHour; i++) {
    if (!changeoverData[asset]) {
      changeoverData[asset] = {
        cip: asset,
        runRateBase,
        resources: {},
      }
    }
    if (!changeoverData[asset]['resources'][resource]) {
      changeoverData[asset]['resources'][resource] = {
        location: row.site_location,
      }
    }

    if (!changeoverData[asset]['resources'][resource][`hour-${i}`]) {
      changeoverData[asset]['resources'][resource][`hour-${i}`] = {
        ...defaultData,
        base_material: [row.base_material],
        base_material_description: [row.base_material_description],
        value: 1,
        changeover_start_time: startTime,
        changeover_end_time: endTime,
        changeover_details: [detail],
        slot_types: [CHANGEOVER],
      }
    } else {
      const hourData = changeoverData[asset]['resources'][resource][
        `hour-${i}`
      ] as ChangeoverRow
      hourData.slot_types.push(CHANGEOVER)
      hourData.base_material?.push(row.base_material)
      hourData.base_material_description.push(row.base_material_description)
      hourData.value++
      hourData.changeover_start_time = startTime
      hourData.changeover_end_time = endTime
      // Appended, not replaced: the scalar pair above keeps only the last
      // writer's window, so the list is the only record of every event
      // that actually touches this hour.
      pushChangeoverDetail(hourData.changeover_details, detail)
    }
  }
}
